import { clientDayKey, warsawParts } from '../../shared/dateTime';

/**
 * Oś czasu harmonogramu — czysta arytmetyka dni i pasków.
 *
 * Dni są **polskie** (Europe/Warsaw), niezależnie od strefy przeglądarki:
 * planista w Niemczech ma widzieć te same doby co w Poznaniu. Pasek i kolumna
 * dnia są liczone z tych samych milisekund, więc doba zmiany czasu (25.10 ma
 * 25 h, 29.03 — 23 h) jest odpowiednio szersza albo węższa, a pasek nie
 * rozjeżdża się z kolumnami.
 */

const DAY_MS = 86_400_000;

const pad = (n: number) => String(n).padStart(2, '0');

const WEEKDAYS = ['nd', 'pn', 'wt', 'śr', 'cz', 'pt', 'sb'];

function keyParts(key: string): [number, number, number] {
  const [y, m, d] = key.split('-').map(Number);
  return [y, m, d];
}

/** Dzień `RRRR-MM-DD` przesunięty o `delta` dni (kalendarz, bez stref). */
export function shiftDayKey(key: string, delta: number): string {
  const [y, m, d] = keyParts(key);
  const t = new Date(Date.UTC(y, m - 1, d) + delta * DAY_MS);
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/**
 * Chwila, w której w Polsce zaczyna się dzień `RRRR-MM-DD`. Zmiana czasu
 * w Polsce wypada o 2:00/3:00, więc północ zawsze istnieje i jest jedna.
 */
export function warsawDayStart(key: string): Date {
  const [y, m, d] = keyParts(key);
  const wall = Date.UTC(y, m - 1, d);
  const offsetAt = (instant: number) => {
    const w = warsawParts(new Date(instant));
    return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - instant;
  };
  // Przesunięcie zależy od chwili, a chwila od przesunięcia — druga próba
  // rozstrzyga dni, w których przesunięcie zmienia się po północy.
  let offset = offsetAt(wall);
  offset = offsetAt(wall - offset);
  return new Date(wall - offset);
}

export interface TimelineWindow {
  from: Date;
  to: Date;
}

export interface TimelineDay {
  key: string;
  start: Date;
  end: Date;
  /** „pn 29.09” */
  label: string;
  /** „pn” — pierwsza linia nagłówka dnia */
  weekday: string;
  /** „29.09” — druga linia nagłówka dnia */
  date: string;
  weekend: boolean;
}

/** Dni polskie okna — od dnia `from` do dnia, w którym wypada `to`. */
export function timelineDays(window: TimelineWindow): TimelineDay[] {
  const days: TimelineDay[] = [];
  const lastKey = clientDayKey(window.to);
  let key = clientDayKey(window.from);
  // Sufit chroni przed pętlą przy zepsutym oknie.
  for (let i = 0; i < 62; i += 1) {
    const [y, m, d] = keyParts(key);
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    const next = shiftDayKey(key, 1);
    days.push({
      key,
      start: warsawDayStart(key),
      end: warsawDayStart(next),
      label: `${WEEKDAYS[weekday]} ${pad(d)}.${pad(m)}`,
      weekday: WEEKDAYS[weekday],
      date: `${pad(d)}.${pad(m)}`,
      weekend: weekday === 0 || weekday === 6,
    });
    if (key === lastKey) break;
    key = next;
  }
  return days;
}

export interface BarGeometry {
  /** Lewa krawędź i szerokość w procentach szerokości okna. */
  left: number;
  width: number;
  /** Pasek zaczyna się przed oknem / kończy po nim — widok ucina brzeg. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

/** Najwęższy pasek, jaki da się jeszcze kliknąć (procent okna). */
export const MIN_BAR_WIDTH = 0.4;

/**
 * Położenie paska w oknie. `null`, gdy brak początku albo pasek w całości
 * poza oknem. Brak końca — pasek jednej chwili (najwęższy klikalny).
 * `minWidth` — najwęższy pasek w procentach; odcinki paska (06.10.2026)
 * rysujemy w prawdziwej szerokości (0), żeby krótka przerwa nie rozpychała
 * sąsiadów.
 */
export function barGeometry(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
  window: TimelineWindow,
  minWidth: number = MIN_BAR_WIDTH,
): BarGeometry | null {
  if (!start) return null;
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : s;
  if (Number.isNaN(s) || Number.isNaN(e)) return null;
  const from = window.from.getTime();
  const to = window.to.getTime();
  const span = to - from;
  if (span <= 0 || e < from || s > to) return null;
  const clippedStart = s < from;
  const clippedEnd = e > to;
  const left = ((Math.max(s, from) - from) / span) * 100;
  const right = ((Math.min(e, to) - from) / span) * 100;
  const width = Math.max(minWidth, right - left);
  return {
    left,
    width: Math.min(width, 100 - left),
    clippedStart,
    clippedEnd,
  };
}

/** Znacznik „teraz” w procentach okna; `null`, gdy teraz jest poza oknem. */
export function nowOffset(now: Date, window: TimelineWindow): number | null {
  const t = now.getTime();
  if (t < window.from.getTime() || t > window.to.getTime()) return null;
  return ((t - window.from.getTime()) / (window.to.getTime() - window.from.getTime())) * 100;
}

/** „PL Poznań → DE Berlin”; brak miejsca — kreska. */
export function routeText(item: {
  fromCountry: string | null;
  fromCity: string | null;
  toCountry: string | null;
  toCity: string | null;
}): string {
  const place = (country: string | null, city: string | null) =>
    [country, city].filter(Boolean).join(' ') || '—';
  return `${place(item.fromCountry, item.fromCity)} → ${place(item.toCountry, item.toCity)}`;
}

/**
 * Rozkład pasków na podtory, żeby nachodzące na siebie nie zasłaniały się
 * nawzajem (warianty jednego auta często się nakładają — to konflikty do
 * pokazania). Pasek idzie na pierwszy tor, na którym poprzedni już się
 * skończył; styk to nie nakładanie. Zwraca numer toru dla każdej pozycji
 * w kolejności wejścia i liczbę torów (co najmniej jeden).
 */
export function packLanes(
  spans: ReadonlyArray<{ start: number; end: number }>,
): { lanes: number[]; count: number } {
  const order = spans
    .map((s, index) => ({ ...s, index }))
    .sort((a, b) => a.start - b.start || a.end - b.end || a.index - b.index);
  const laneEnds: number[] = [];
  const lanes = spans.map(() => 0);
  for (const s of order) {
    let lane = laneEnds.findIndex((end) => end <= s.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(s.end);
    } else {
      laneEnds[lane] = s.end;
    }
    lanes[s.index] = lane;
  }
  return { lanes, count: Math.max(1, laneEnds.length) };
}

/** Chwila z API jako milisekundy; brak albo zła data — `null`. */
function ms(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Miejsce, które pozycja zajmuje na osi: okno zlecenia (szeroki obszar
 * linią przerywaną) razem z paskiem szacunku. Do rozkładu na podtory —
 * okna kolejnych zleceń auta nachodzą na siebie (mediana 31,5 h), a okno
 * jednego zlecenia nie może zasłaniać paska drugiego. Odcinki paska
 * (06.10.2026) też się liczą: dolot stoi przed oknem załadunku i nie może
 * wjechać na pasek poprzedniego zlecenia. Bez żadnej daty — `null` (pozycji
 * nie rysujemy).
 */
export function planItemSpan(item: {
  windowFrom: string | null;
  windowTo: string | null;
  occupancyFrom: string | null;
  occupancyTo: string | null;
  segments?: ReadonlyArray<{ start: string; end: string }>;
  /** Odcinek „wg planu TMS” za paskiem (12.10.2026) — też zajmuje miejsce na osi. */
  tmsPlanSegment?: { start: string; end: string };
}): { start: number; end: number } | null {
  const segmentPoints = [...(item.segments ?? []), ...(item.tmsPlanSegment ? [item.tmsPlanSegment] : [])].flatMap(
    (s) => [s.start, s.end],
  );
  const points = [item.windowFrom, item.windowTo, item.occupancyFrom, item.occupancyTo, ...segmentPoints]
    .map(ms)
    .filter((t): t is number => t !== null);
  if (points.length === 0) return null;
  return { start: Math.min(...points), end: Math.max(...points) };
}

/** Dzisiejszy dzień polski jako `RRRR-MM-DD`. */
export function todayKey(now: Date = new Date()): string {
  return clientDayKey(now);
}
